"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

import { getSettingsNavGroups } from "@/config/navigation";
import { useLastAppLocation } from "@/hooks/useLastAppLocation";
import { cn } from "@/lib/utils";
import {
  SidebarCollapsibleGroup,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderMenu,
  SidebarHeaderMenuButton,
  SidebarHeaderRow,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuButtonContent,
  SidebarMenuItem,
  SidebarScrollArea,
} from "@gogive/design-system/components/sidebar";
import type { LucideIcon } from "lucide-react";

function SettingsNavLink({
  name,
  href,
  Icon,
}: {
  name: string;
  href: string;
  Icon: LucideIcon;
}) {
  const pathname = usePathname() ?? "/";
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const normalizedHref = href.replace(/\/+$/, "") || "/";
  const active =
    normalizedPath === normalizedHref ||
    normalizedPath.startsWith(`${normalizedHref}/`);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active}>
        <Link
          href={href}
          className={cn("[&_[data-slot=icon]]:text-muted-foreground")}
        >
          <Icon className="size-4 shrink-0" data-slot="icon" />
          <span>{name}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SettingsNavContent({ isAdmin }: { isAdmin: boolean }) {
  const backTo = useLastAppLocation();
  const groups = useMemo(() => getSettingsNavGroups(isAdmin), [isAdmin]);

  return (
    <>
      <SidebarHeader>
        <SidebarHeaderRow>
          <SidebarHeaderMenu>
            <SidebarMenuItem>
              <SidebarHeaderMenuButton asChild>
                <Link href={backTo}>
                  <SidebarMenuButtonContent icon="ArrowLeft">
                    Back to app
                  </SidebarMenuButtonContent>
                </Link>
              </SidebarHeaderMenuButton>
            </SidebarMenuItem>
          </SidebarHeaderMenu>
        </SidebarHeaderRow>
      </SidebarHeader>
      <SidebarContent className="min-h-0 flex-1 gap-0 overflow-hidden px-0 py-0">
        <SidebarScrollArea className="px-0">
          {groups.map((group) => (
            <SidebarCollapsibleGroup key={group.id} title={group.label}>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SettingsNavLink
                    key={item.path}
                    name={item.name}
                    href={item.path}
                    Icon={item.icon}
                  />
                ))}
              </SidebarMenu>
            </SidebarCollapsibleGroup>
          ))}
        </SidebarScrollArea>
      </SidebarContent>
    </>
  );
}
